import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  actOnRadarOpportunity,
  actOnRadarToken,
  actInRadarPursuit,
  archiveRadarTracker,
  buildRadarV2DraftBundles,
  buildRadarV2Shadow,
  commitRadarFileIntake,
  confirmRadarV2Intake,
  dispatchRadarEmails,
  fetchRadarAdminStatus,
  fetchRadarArchives,
  fetchRadarCollaborationStatus,
  fetchRadarPursuitProofs,
  fetchRadarV2AdminStatus,
  fetchRadarV2IntakeQueue,
  fetchRadarTrackingStatus,
  fetchRadarStatus,
  previewRadarFileIntake,
  previewRadarV2Engine,
  queueRadarReadyAlerts,
  recheckRadarIdentityRequest,
  removeRadarDistributionMember,
  resolveRadarIdentityRequest,
  resolveRadarToken,
  runRadarNow,
  runRadarPreview,
  saveRadarHuddle,
  stageRadarV2Intake,
  setRadarArchiveAccess,
  trackCompanyInRadar,
  updateRadarPursuit,
  updateRadarSettings,
  updateRadarSource,
  upsertRadarDistributionMember,
  type RadarFileIntakePayload,
  type RadarPublicOpportunity,
  type RadarPursuit,
} from "./api";

export function useRadarStatus() {
  return useQuery({
    queryKey: ["radar", "status"],
    queryFn: fetchRadarStatus,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useRadarAdminStatus() {
  return useQuery({
    queryKey: ["radar", "admin-status"],
    queryFn: fetchRadarAdminStatus,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useRadarCollaborationStatus() {
  return useQuery({
    queryKey: ["radar", "collaboration-status"],
    queryFn: fetchRadarCollaborationStatus,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useRadarPursuitProofs(enabled: boolean) {
  return useQuery({
    queryKey: ["radar", "pursuit-proofs"],
    queryFn: fetchRadarPursuitProofs,
    enabled,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useRadarArchives(scope: "all" | "my", search: string, enabled: boolean) {
  return useQuery({
    queryKey: ["radar", "archives", scope, search.trim().toLowerCase()],
    queryFn: () => fetchRadarArchives(scope, search),
    enabled,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useRadarPursuitRoom() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      pursuitId,
      roomAction,
      fields,
    }: {
      pursuitId: string;
      roomAction:
        | "comment"
        | "mute"
        | "leave"
        | "resolve_lead"
        | "invite"
        | "follow_up"
        | "archive";
      fields?: Record<string, unknown>;
    }) => actInRadarPursuit(pursuitId, roomAction, fields),
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useRadarOpportunityAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      alertRecipientId,
      recipientAction,
      fields,
    }: {
      alertRecipientId: string;
      recipientAction: "claim" | "pass" | "snooze" | "not_relevant" | "field_intel";
      fields?: Record<string, unknown>;
    }) => actOnRadarOpportunity(alertRecipientId, recipientAction, fields),
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useRadarTrackingStatus(organizationId: string | null, companyName: string) {
  return useQuery({
    queryKey: ["radar", "tracking-status", organizationId ?? companyName.trim().toLowerCase()],
    queryFn: () => fetchRadarTrackingStatus({ organizationId, companyName }),
    enabled: Boolean(organizationId || companyName.trim()),
    staleTime: 30_000,
    retry: false,
  });
}

export function useTrackCompanyInRadar(organizationId: string | null, companyName: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => trackCompanyInRadar({ organizationId, companyName }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useArchiveRadarTracker() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (trackerId: string) => archiveRadarTracker(trackerId),
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useRecheckRadarIdentityRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (identityRequestId: string) => recheckRadarIdentityRequest(identityRequestId),
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useResolveRadarIdentityRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      identityRequestId,
      organizationId,
    }: {
      identityRequestId: string;
      organizationId: string;
    }) => resolveRadarIdentityRequest(identityRequestId, organizationId),
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}
export function useUpdateRadarPursuit() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ pursuitId, stage }: { pursuitId: string; stage: RadarPursuit["stage"] }) =>
      updateRadarPursuit(pursuitId, stage),
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useRadarPreview() {
  return useMutation({ mutationFn: runRadarPreview });
}

export function useRadarV2AdminStatus(enabled = true) {
  return useQuery({
    queryKey: ["radar", "v2", "status"],
    queryFn: fetchRadarV2AdminStatus,
    enabled,
    staleTime: 15_000,
    retry: 1,
  });
}

export function useRadarV2IntakeQueue(enabled = true) {
  return useQuery({
    queryKey: ["radar", "v2", "intake-queue"],
    queryFn: fetchRadarV2IntakeQueue,
    enabled,
    staleTime: 15_000,
    retry: 1,
  });
}

export function useRadarV2Preview() {
  return useMutation({ mutationFn: previewRadarV2Engine });
}

export function useRadarV2ShadowBuild() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: buildRadarV2Shadow,
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar", "v2"] }),
  });
}

export function useRadarV2DraftBundles() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: buildRadarV2DraftBundles,
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar", "v2"] }),
  });
}

export function useConfirmRadarV2Intake() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: confirmRadarV2Intake,
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar", "v2"] }),
  });
}

export function useStageRadarV2Intake() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: stageRadarV2Intake,
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar", "v2"] }),
  });
}

export function useRadarRun() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: runRadarNow,
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useQueueRadarReadyAlerts() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: queueRadarReadyAlerts,
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useDispatchRadarEmails() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: dispatchRadarEmails,
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useSaveRadarHuddle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      practiceKey,
      weekStart,
      notes,
    }: {
      practiceKey: string;
      weekStart: string;
      notes: string;
    }) => saveRadarHuddle(practiceKey, weekStart, notes),
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useUpdateRadarSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: updateRadarSettings,
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useSetRadarArchiveAccess() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ email, enabled }: { email: string; enabled: boolean }) =>
      setRadarArchiveAccess(email, enabled),
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useRadarFilePreview() {
  return useMutation({
    mutationFn: (payload: RadarFileIntakePayload) => previewRadarFileIntake(payload),
  });
}

export function useRadarFileCommit() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: RadarFileIntakePayload) => commitRadarFileIntake(payload),
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useUpdateRadarSource() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceKey, enabled }: { sourceKey: string; enabled: boolean }) =>
      updateRadarSource(sourceKey, enabled),
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useUpsertRadarDistributionMember() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: upsertRadarDistributionMember,
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useRemoveRadarDistributionMember() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: removeRadarDistributionMember,
    onSuccess: () => client.invalidateQueries({ queryKey: ["radar"] }),
  });
}

export function useRadarToken(token: string) {
  return useQuery({
    queryKey: ["radar", "token", token],
    queryFn: () => resolveRadarToken(token),
    enabled: Boolean(token),
    retry: false,
  });
}

export function useRadarTokenAction(token: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ action, fields }: { action: string; fields?: Record<string, unknown> }) => {
      const resolved = client.getQueryData<RadarPublicOpportunity>(["radar", "token", token]);
      return actOnRadarToken(token, action, fields, resolved?.actionNonce);
    },
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ["radar", "token", token] }),
        client.invalidateQueries({ queryKey: ["radar", "status"] }),
      ]),
  });
}
